/**
 * Where the browser is allowed to ask for help reaching the other device.
 *
 * Asked of the service rather than compiled in, because the relay password
 * expires every ten minutes and a compiled-in one would have to be permanent.
 *
 * What comes back is checked before it is used. The service is ours, but the
 * whole design assumes the thing in the middle can be wrong or replaced, and
 * an unchecked list here is a way to point somebody's browser at an address
 * of someone else's choosing.
 */

import { CONNECT_URL } from "./signalling";

/** What to use when the service cannot be reached: what the app had before. */
export const FALLBACK_ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }];

/** The only schemes a helper address may use. Nothing else is a helper. */
const ALLOWED = /^(stuns?|turns?):/;

/**
 * Keeps the entries that are actually helper addresses, drops the rest.
 *
 * A list that comes back empty or unusable is not an error worth showing
 * anyone: it means "try the way you already know".
 */
export function usableServers(payload) {
  const listed = Array.isArray(payload?.iceServers) ? payload.iceServers : [];
  const kept = listed
    .map((entry) => {
      const urls = (Array.isArray(entry?.urls) ? entry.urls : [entry?.urls]).filter(
        (url) => typeof url === "string" && ALLOWED.test(url)
      );
      if (!urls.length) return null;
      const server = { urls };
      // Only carried through for a relay, and only as given. Anything else
      // in the entry is ignored rather than passed to the browser.
      if (typeof entry.username === "string") server.username = entry.username;
      if (typeof entry.credential === "string") server.credential = entry.credential;
      return server;
    })
    .filter(Boolean);
  return kept.length ? kept : FALLBACK_ICE_SERVERS;
}

/**
 * Asks the service, and falls back rather than failing.
 *
 * A phone that cannot reach the service still has a working direct path on
 * the same wifi, so a failure here must not stop the attempt.
 */
export async function fetchIceServers({
  base = CONNECT_URL,
  fetchImpl = typeof fetch !== "undefined" ? fetch : null,
  timeoutMs = 4000,
  now = () => Date.now(),
} = {}) {
  if (!fetchImpl) return FALLBACK_ICE_SERVERS;
  const stop = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = setTimeout(() => stop?.abort(), timeoutMs);
  try {
    // The moment is in the address on purpose, so no cache anywhere has this
    // exact url to answer with. A service worker installed before tonight
    // still holds the old rules and would hand back a password from an hour
    // ago; the relay then refuses it and the failure looks like a broken
    // relay. Belt as well as braces: the rules are fixed too.
    const response = await fetchImpl(`${base.replace(/\/$/, "")}/ice?t=${now()}`, {
      signal: stop?.signal,
      cache: "no-store",
    });
    if (!response?.ok) return FALLBACK_ICE_SERVERS;
    return usableServers(await response.json());
  } catch {
    return FALLBACK_ICE_SERVERS;
  } finally {
    clearTimeout(timer);
  }
}
